/**
 * Perfils (§2.2), sense zod, per a la part de la web que no ha de carregar la llibreria (§3.3).
 * Ordre: de més a menys permisos. Ha de coincidir amb roleSchema (roles.ts); un test ho comprova.
 */
export const ROLES = ['superadmin', 'agent', 'client'];
export function isRole(value) {
    return typeof value === 'string' && ROLES.includes(value);
}
/** Llegeix els perfils d'un custom claim, descartant valors desconeguts. Sempre inclou `client`. */
export function parseRoles(value) {
    const list = Array.isArray(value) ? value.filter(isRole) : [];
    return normalizeRoles(list);
}
/** Sense duplicats, en l'ordre de ROLES i amb `client` sempre present (§2.2). */
export function normalizeRoles(roles) {
    return ROLES.filter((r) => r === 'client' || roles.includes(r));
}
/** El perfil amb més permisos de la llista. */
export function highestRole(roles) {
    return ROLES.find((r) => roles.includes(r)) ?? 'client';
}
/** Perfil actiu efectiu: el del claim si és dins `roles`; si no, cap (com fan les regles). */
export function effectiveActiveRole(activeRole, roles) {
    return isRole(activeRole) && roles.includes(activeRole) ? activeRole : null;
}
export function isStaffRole(role) {
    return role === 'agent' || role === 'superadmin';
}
/**
 * Perfils que ha de dur el token a partir del document `users/{uid}`, que és la font de veritat
 * (§2.2): s'hi poden concedir perfils des de l'app o editant-lo a la consola de Firebase.
 * - Perfils: els del document (sempre amb `client`).
 * - Perfil actiu: si els perfils no han canviat i l'actiu continua concedit, es manté; si han
 *   canviat, passa al de més permisos (qui acaba de rebre el perfil d'agent entra com a agent).
 * `changed`: si el token s'ha d'actualitzar.
 */
export function syncProfileClaims(docRoles, claims) {
    const roles = parseRoles(docRoles);
    const hadClaims = Array.isArray(claims?.roles);
    const current = parseRoles(claims?.roles);
    const sameRoles = hadClaims && current.length === roles.length && current.every((r, i) => r === roles[i]);
    const active = effectiveActiveRole(claims?.activeRole, roles);
    const activeRole = sameRoles && active !== null ? active : highestRole(roles);
    return { roles, activeRole, changed: !sameRoles || claims?.activeRole !== activeRole };
}
/**
 * Mòduls que el superadmin concedeix a agents concrets (§2.3). Com els perfils, la font de veritat és
 * `users/{uid}.modules`, i el token en porta una còpia (claim `modules`) que llegeixen les regles.
 */
export const MODULES = ['opportunities'];
export function parseModules(value) {
    const list = Array.isArray(value) ? value : [];
    return MODULES.filter((m) => list.includes(m));
}
/** Pot fer servir el mòdul amb el perfil actiu: el superadmin sempre; un agent, si li han concedit. */
export function canUseModule(activeRole, modules, module) {
    return activeRole === 'superadmin' || (activeRole === 'agent' && modules.includes(module));
}
export function sameModules(a, b) {
    return a.length === b.length && a.every((m, i) => m === b[i]);
}
